import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MonitorPlay, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Watch Party embedded player + URL input. Sits above the room chat.
 * Backend parses the URL and broadcasts updates to all room members via the
 * existing room WS so everyone sees the same stream simultaneously.
 */
export const WatchParty = ({ room, watchParty }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/rooms/${room.code}/watch-party`, { url: url.trim() });
      setUrl('');
      toast.success('Stream started for everyone in the room');
      // No manual state update — the WS broadcast updates `watchParty` prop
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start stream');
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/rooms/${room.code}/watch-party`);
      toast.message('Watch party stopped');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not stop stream');
    } finally {
      setLoading(false);
    }
  };

  const embedUrl = (() => {
    if (!watchParty) return null;
    const { provider, embed_id, type } = watchParty;
    if (provider === 'youtube') {
      return `https://www.youtube.com/embed/${embed_id}?autoplay=0&rel=0`;
    }
    if (provider === 'twitch') {
      const host = window.location.hostname;
      if (type === 'video') {
        return `https://player.twitch.tv/?video=${embed_id}&parent=${host}&autoplay=false`;
      }
      return `https://player.twitch.tv/?channel=${embed_id}&parent=${host}&autoplay=false`;
    }
    return null;
  })();

  return (
    <div
      className="bg-[#09090B] border-b border-[#E4E4E7]"
      data-testid="watch-party-container"
    >
      {watchParty && embedUrl ? (
        <>
          <div className="flex items-center justify-between px-3 py-2 bg-[#09090B]">
            <div className="flex items-center gap-2 min-w-0">
              <MonitorPlay size={16} weight="fill" className="text-[#0047FF]" />
              <span className="text-white text-xs truncate" data-testid="watch-party-source">
                {watchParty.provider === 'youtube' ? 'YouTube' : 'Twitch'} ·{' '}
                started by {watchParty.set_by_username}
              </span>
            </div>
            <Button
              onClick={clear}
              disabled={loading}
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-white hover:bg-white/10"
              data-testid="watch-party-clear-button"
            >
              <X size={14} />
            </Button>
          </div>
          <div className="aspect-video w-full bg-black">
            <iframe
              key={embedUrl}
              src={embedUrl}
              title="Watch Party"
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
              data-testid="watch-party-iframe"
            />
          </div>
          <div className="flex gap-2 p-2 bg-[#09090B] border-t border-white/10">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && submit()}
              placeholder="Replace with another Twitch or YouTube link"
              className="flex-1 h-8 bg-white/5 border-white/10 text-white text-xs placeholder:text-white/40"
              data-testid="watch-party-replace-input"
            />
            <Button
              onClick={submit}
              disabled={loading || !url.trim()}
              size="sm"
              className="h-8 px-3 bg-[#0047FF] hover:bg-[#0037CC] text-white"
              data-testid="watch-party-replace-button"
            >
              Replace
            </Button>
          </div>
        </>
      ) : (
        <div className="flex gap-2 p-3 bg-white">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && submit()}
            placeholder="Paste a Twitch or YouTube link to start a watch party"
            className="flex-1 h-9 border-[#E4E4E7]"
            data-testid="watch-party-url-input"
          />
          <Button
            onClick={submit}
            disabled={loading || !url.trim()}
            size="sm"
            className="h-9 px-3 bg-[#0047FF] hover:bg-[#0037CC] text-white"
            data-testid="watch-party-start-button"
          >
            <MonitorPlay size={14} weight="fill" className="mr-1" />
            Start
          </Button>
        </div>
      )}
    </div>
  );
};
